import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  Animated,
  Easing,
  Dimensions,
  Platform,
  View,
  Image,
  StyleSheet,
  StatusBar,
  ScrollView,
  Text,
  TouchableOpacity,
} from 'react-native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';

import Languages from '../../constants/Languages';
import Colors from '../../constants/Colors';
import Layout from '../../constants/Layout';
import FilledButton from '../FilledButton';
import Header from './Header';

const styles = StyleSheet.create({
  container: {
    backgroundColor: Colors.mediumBlue,
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  topContainer: {
    flex: 1,
    alignItems: 'center',
  },
  infoText: {
    fontFamily: 'roboto-regular',
    lineHeight: 24,
    fontSize: 16,
    color: Colors.white,
    textAlign: 'center',
  },
  languageItem: {
    display: 'flex',
    flexDirection: 'row',
    flex: 1,
    justifyContent: 'space-between',
    alignItems: 'center',
    height: 56,
    width: Dimensions.get('window').width - 32,
    maxWidth: 380,
    marginLeft: 16,
    marginRight: 16,
    borderBottomWidth: 1,
    borderBottomColor: Colors.lightBlue,
  },
  languageText: {
    fontFamily: 'roboto-regular',
    lineHeight: 56,
    fontSize: 16,
    flex: 8,
    color: Colors.white,
  },
  circleOuter: {
    borderWidth: 1,
    borderColor: Colors.white,
    width: 17,
    height: 17,
    borderRadius: 17 / 2,
    padding: 1,
    marginLeft: 10,
  },
  circleInner: {
    backgroundColor: Colors.white,
    width: 13,
    height: 13,
    borderRadius: 13 / 2,
  },
  headingContainer: {
    display: 'flex',
    flexDirection: 'row',
    height: 56,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headingText: {
    fontSize: 18,
    color: Colors.white,
    fontFamily: 'roboto-regular',
    textAlign: 'center',
  },
  contentContainer: {
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  contentArea: {
    flex: 1,
    alignItems: 'center',
  },
  languageListContainer: {
    flex: 1,
    width: '100%',
  },
  languageScrollView: {
    flex: 1,
  },
});

class SelectLanguage extends Component {
  static propTypes = {
    checkedItem: PropTypes.number.isRequired,
    onChange: PropTypes.func.isRequired,
    goNext: PropTypes.func.isRequired,
    goBack: PropTypes.func.isRequired,
    translation: PropTypes.object.isRequired,
  };

  constructor(props) {
    super(props);
    this.animated1 = new Animated.Value(0); // 0-300
    this.animated2 = new Animated.Value(0); // 600-900
    this.animated3 = new Animated.Value(0); // 800-1100

    Animated.timing(this.animated1, {
      toValue: 1,
      duration: 300,
      easing: Easing.ease,
      useNativeDriver: false,
    }).start();

    Animated.timing(this.animated2, {
      toValue: 1,
      duration: 300,
      delay: 600,
      easing: Easing.ease,
      useNativeDriver: false,
    }).start();

    Animated.timing(this.animated3, {
      toValue: 1,
      duration: 300,
      delay: 800,
      easing: Easing.ease,
      useNativeDriver: false,
    }).start();
  }

  animated1 = null;
  animated2 = null;
  animated3 = null;

  renderContent(insets, isInTabContext) {
    const { checkedItem, onChange, goNext, goBack, translation } = this.props;
    const introText = translation.registration_intro;
    const selectLanguage = translation.fragment_registration_language_textview_title;
    const next = translation.fragment_registration_language_button_next;

    const items = [];
    Languages.languages.forEach((lang, index) => {
      const onCheck = () => {
        onChange(index);
      };
      items.push(
        <TouchableOpacity
          key={lang.name}
          style={styles.languageItem}
          onPress={onCheck}
          testID={`language-${lang.name}`}
        >
          <Text style={styles.languageText}>{lang.label}</Text>
          {index === checkedItem ? (
            <View style={styles.circleOuter}>
              <View style={styles.circleInner} />
            </View>
          ) : null}
        </TouchableOpacity>
      );
    });

    const height = Dimensions.get('window').height;
    const halfHeight = Math.floor(height / 2);
    const headerHeight = 56;
    const topMargin = Platform.OS === 'web' ? 50 : 10;
    const contentTopMargin = Platform.OS === 'web' ? 200 : 140;
    const startingPosition = halfHeight - insets.top;

    return (
      <View style={styles.container} testID="language">
        {Platform.OS === 'ios' && <StatusBar barStyle="light-content" />}
        <Header goBack={goBack} />
        <Animated.View
          style={{
            position: 'absolute',
            top: startingPosition,
            width: 270,
            alignItems: 'center',
            transform: [
              {
                translateY: this.animated1.interpolate({
                  inputRange: [0, 1],
                  outputRange: [0, -(startingPosition - (headerHeight + topMargin))],
                }),
              },
            ],
          }}
        >
          <Animated.View
            style={{
              transform: [
                {
                  scaleX: this.animated1.interpolate({
                    inputRange: [0, 1],
                    outputRange: [1, 1.33],
                  }),
                },
                {
                  scaleY: this.animated1.interpolate({
                    inputRange: [0, 1],
                    outputRange: [1, 1.33],
                  }),
                },
              ],
            }}
          >
            <TouchableOpacity onPress={goBack}>
              <Image
                source={require('../../assets/images/plentiful_logo_and_label.png')}
                resizeMode="cover"
                style={{
                  height: 56,
                  width: 147,
                  resizeMode: 'contain',
                }}
              />
            </TouchableOpacity>
          </Animated.View>
          <Animated.View
            style={{
              width: 270,
              opacity: this.animated2,
              marginTop: 20,
            }}
          >
            <Text style={styles.infoText}>{introText}</Text>
          </Animated.View>
        </Animated.View>
        <View
          style={{
            flex: 1,
            marginTop: insets.top + contentTopMargin,
            alignItems: 'center',
          }}
        >
          <Animated.View
            style={{
              width: '100%',
              opacity: this.animated3,
              flex: 1,
              alignItems: 'center',
            }}
          >
            <View style={styles.contentArea}>
              <View style={styles.headingContainer}>
                <Text testID={`select-language-text`} style={styles.headingText}>
                  {selectLanguage}
                </Text>
              </View>
              <View style={styles.languageListContainer}>
                <ScrollView style={styles.languageScrollView} contentContainerStyle={styles.contentContainer}>
                  {items}
                </ScrollView>
              </View>
            </View>
            <View style={{ ...Layout.signupFooter, ...(isInTabContext && { paddingTop: 16 }) }}>
              <FilledButton
                onPress={goNext}
                label={next}
                color={Colors.white}
                textColor={Colors.mediumBlue}
                backgroundColor={Colors.white}
                full
              />
            </View>
          </Animated.View>
        </View>
      </View>
    );
  }

  render() {
    return <SelectLanguageWithNavigation {...this.props} component={this} />;
  }
}

// Functional component wrapper to detect navigation context and handle SafeAreaView
const SelectLanguageWithNavigation = ({ component, ...props }) => {
  const navigation = useNavigation();

  // Detect if we're in tab context vs stack context
  // When accessed via language tab: SelectLanguage IS at tab navigator level (type: "tab")
  // When accessed via stack navigation: SelectLanguage is in a stack navigator
  const currentState = navigation.getState();
  const isInTabContext = currentState?.type === 'tab';

  console.log('SelectLanguage Context Detection:', {
    currentNavType: currentState?.type,
    isInTabContext,
    routeName: currentState?.routeNames?.[currentState?.index],
  });

  // Use conditional SafeAreaView edges based on context
  const edges = isInTabContext
    ? ['top', 'left', 'right'] // Tab handles bottom edge
    : ['top', 'left', 'right', 'bottom']; // Stack must handle bottom edge

  return (
    <SafeAreaView style={{ flex: 1 }} edges={edges}>
      <SelectLanguageContent component={component} isInTabContext={isInTabContext} {...props} />
    </SafeAreaView>
  );
};

// Functional component wrapper to render content
const SelectLanguageContent = ({ component, isInTabContext, ...props }) => {
  const insets = useSafeAreaInsets();
  return component.renderContent(insets, isInTabContext);
};

export default SelectLanguage;
